import { isConfigValid } from 'alchemie-scorer';
import { ContentType, LessonMode, QuestionFeedbackState, ScoreState, ScoringOption } from '../../Constants';
import { register } from '../../i18n';
import { confirmDialog, dependentAlert } from '../components/dialogs';
import audioManager from '../managers/AudioManager';
import lessonManager from '../managers/LessonManager';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';
import responseManager from '../managers/ResponseManager';
import studentActivityManager from '../managers/StudentActivityManager';
import timeManager from '../managers/TimeManager';
import toolbarManager from '../managers/ToolbarManager';
import userManager from '../managers/UserManager';
import { QFMFlagKeys } from '../managers/obseverableObjects/QuestionFeedbackMode';
import Auth from './AuthService';
// eslint-disable-next-line import/no-cycle
import questionService from './QuestionService';
import scoringService from './ScoringService';
// eslint-disable-next-line import/no-cycle
import studentActivityService from './StudentActivityService';
import UtilsService from './UtilsService';
import createQuestionBehavior from '../questionBehavior/BehaviorFactory';
import accessibilityService from './AccessibilityService';

const t = register('GlobalQuestionLabels');

class ResponseService {
  loadSlide(lessonElementId, mode) {
    const model = lessonManager.getSlideModel(lessonElementId);
    const state = responseManager.getLessonElementState(lessonElementId);
    const { noSubmit } = questionFeedbackManager;

    if (!model) {
      return;
    }
    responseManager.setReadOnly(lessonElementId, (state && state.isSubmitted && !noSubmit));

    if (model && (state || model.isStudentInstruction) && model.isActivityPart) {
      const parentElement = lessonManager.getLessonElement(model.parentActivityElementId);
      if (parentElement && parentElement.lessonElementIds && parentElement.lessonElementIds.length > 0) {
        parentElement.lessonElementIds.forEach((leid) => {
          if (leid !== lessonElementId) {
            const childModel = lessonManager.getSlideModel(leid);
            const childState = responseManager.getLessonElementState(leid);
            this.doFeedbackLogic(leid, childModel, mode, childState);
          }
        });
      }
    }
    this.doFeedbackLogic(lessonElementId, model, mode, state);
  }

  async submitButtonHandler(event, lessonElementId) {
    questionFeedbackManager.setSubmitVisibleState(lessonElementId, false);
    questionFeedbackManager.setLoadingButtonState(lessonElementId, true);
    const mode = lessonManager.playerMode;
    const model = lessonManager.getSlideModel(lessonElementId);
    const state = responseManager.getLessonElementState(lessonElementId);
    await this.flushBufferedInteractiveMessage(lessonElementId);
    const currentResponse = state.currentResponseAnswer;

    const responseIsOk = responseManager.checkForValidResponse(lessonElementId, model);
    if (responseIsOk === null) {
      questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
      questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
      throw new Error('checkForValidResponse returned null');
    }
    // responseIsOk can be a boolean or it can be an object with keys 'isValid', 'title', 'text', 'allowSubmit'
    // If it is a boolean, default values are used

    const isValid = responseIsOk === true || (responseIsOk && responseIsOk.isValid);
    if (!isValid && !state.isSubmitted && !model.unscored) {
      let dialogObject;
      const canSubmit = typeof responseIsOk === 'boolean'
        || !('allowSubmit' in responseIsOk)
        || responseIsOk.allowSubmit;

      if (currentResponse) {
        dialogObject = {
          title: `${t('notSubmittedBody')}`,
          text: `${t('cannotBe')}`,
        };
      }

      if (typeof responseIsOk === 'object') {
        dialogObject = {
          ...dialogObject,
          ...responseIsOk
        };
        if (!canSubmit) {
          dialogObject.showCancelButton = false;
          dialogObject.confirmButtonText = `${t('ok')}`;
        }
      }

      const dialogResult = await confirmDialog(dialogObject);
      if (dialogResult.isDismissed || !canSubmit) {
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
        questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
        const subButton = document.getElementById(`submit-button-${lessonElementId}`);
        if (subButton) {
          subButton.focus();
        }
        return;
      }
    }

    if (model.unscored && !isValid) {
      questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
      questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
      return;
    }

    const success = await this.handleSubmitResponse(lessonElementId);

    if (!success) {
      questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
      questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
      return;
    }

    const isAutoScored = state.questionBehavior.isAutoScored(model);
    const { hasFeedback } = questionFeedbackManager;
    if (isAutoScored && hasFeedback &&
        ((mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) ||
         mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION)) {
      await responseManager.scoreResponse(model, lessonElementId);
      if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
        responseManager.setPreviewResponseSubmitted(lessonElementId, true);
      }
      const isQuestionCorrect = responseManager.isQuestionCorrect(lessonElementId);
      let incorrectImageUrl = null;
      let correctImageUrl = null;
      let currentImage = null;

      if (model && model.imageFeedback && model.imageFeedback.length > 0) {
        model.imageFeedback.forEach((f) => {
          if (f.type === 'correct') {
            correctImageUrl = `url(${Auth.getResourceUrlByResourceId(f.resourceId)})`;
          }
          if (f.type === 'incorrect') {
            incorrectImageUrl = `url(${Auth.getResourceUrlByResourceId(f.resourceId)})`;
          }
        });
      }

      if (isQuestionCorrect === QuestionFeedbackState.CORRECT) {
        currentImage = correctImageUrl;
      }

      if (isQuestionCorrect === QuestionFeedbackState.INCORRECT) {
        currentImage = incorrectImageUrl;
      }

      // Announce feedback to screen readers immediately
      const feedbackText = this.getQuestionFeedbackText(lessonElementId);
      accessibilityService.announceSubmissionResult(
        isQuestionCorrect === QuestionFeedbackState.CORRECT,
        feedbackText
      );

      if (currentImage) {
        questionFeedbackManager.setShowFeedbackImages(lessonElementId, true);
        setTimeout(() => {
          questionFeedbackManager.setShowFeedbackImages(lessonElementId, false);
        }, 3000);
      }

      if (model && model.audioFeedback && model.audioFeedback.length > 0) {
        let audio = null;

        if (isQuestionCorrect === QuestionFeedbackState.CORRECT) {
          audio = model.audioFeedback.find((element) => element.type === 'correct');
        }

        if (isQuestionCorrect === QuestionFeedbackState.INCORRECT) {
          audio = model.audioFeedback.find((element) => element.type === 'incorrect');
        }

        if (audio) {
          const audioSrc = `${Auth.ecms}/api/redirectToStreamUrl?authKey=${Auth.authKey}&resourceFolder=userFiles&entityId=${audio.resourceId}&entityTypeId=audio_resource`;
          audioManager.playAudio(audioSrc);
        }
      }

      const shouldBeCached = responseManager.shouldBeCached(state, model, state.currentResponseAnswer);
      if (shouldBeCached) {
        state.setCachedResponse(UtilsService.safeMobxClone(state.currentResponseAnswer), state.submittedItemScore);
      }
    } else if (model.isTestItem && !isAutoScored && hasFeedback &&
        (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW || mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION)) {
      if (!model.unscored) {
        responseManager.addNotScoredList(lessonElementId);
      }

      // Announce that answer was saved for manually scored items
      const feedbackText = this.getQuestionFeedbackText(lessonElementId);
      if (feedbackText) {
        accessibilityService.announceMessage(feedbackText);
      }
    }
    if (state.isSubmitted && state.isTestItem) {
      const isQuestionCorrect = responseManager.isQuestionCorrect(lessonElementId);
      const { isPractice, practiceAttempts } = questionFeedbackManager.questionFeedbackMode;

      const currentUiMode = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
      const { currentAttemptCount } = currentUiMode;
      if (model.type !== ContentType.ALCHEMIE_TEST_ITEM.type || !isPractice) {
        this.doDependencyLogic(lessonElementId);
      } else if (isPractice && (isQuestionCorrect === QuestionFeedbackState.CORRECT || currentAttemptCount >= practiceAttempts)) {
        this.doDependencyLogic(lessonElementId);
      }
    }
    this.doFeedbackLogic(lessonElementId, model, mode, state);
  }

  tryAgainButtonHandler = async (event, lessonElementId) => {
    questionFeedbackManager.setTryAgainVisibleState(lessonElementId, false);
    const mode = lessonManager.playerMode;
    const model = lessonManager.getSlideModel(lessonElementId);
    let lessonElementState = responseManager.getLessonElementState(lessonElementId);
    responseManager.setPreviewResponseSubmitted(lessonElementId, false);

    if (!!event && lessonElementState?.questionBehavior?.tryAgainButtonHandler) {
      const result = await lessonElementState.questionBehavior.tryAgainButtonHandler({
        event,
        lessonElementId,
        lessonElementState,
        model
      });
      if (result?.lessonElementState) {
        lessonElementState = result.lessonElementState;
      }
    }

    this.doFeedbackLogic(lessonElementId, model, mode, lessonElementState);
    responseManager.setReadOnly(lessonElementId, false);
    questionFeedbackManager.addButtonUsage('tryAgain');
  }

  showAnswersButtonHandler = async (event, lessonElementId, toggle) => {
    const mode = lessonManager.playerMode;
    const wrongPracticeMode = (mode === LessonMode.SCORING || mode === LessonMode.REVIEW);
    const { hasFeedback } = questionFeedbackManager;
    const { isPractice } = questionFeedbackManager;
    const currentUiMode = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    const { currentAttemptCount } = currentUiMode;
    const { practiceAttempts } = questionFeedbackManager.questionFeedbackMode;
    const isPracticeRunning = (isPractice && !wrongPracticeMode && (currentAttemptCount < practiceAttempts));

    if (hasFeedback || mode === LessonMode.REVIEW || mode === LessonMode.SCORING) {
      const state = responseManager.getLessonElementState(lessonElementId);
      const model = lessonManager.getSlideModel(lessonElementId);
      if (state.questionBehavior && model.validation && toggle) {
        if (state.questionBehavior.isAutoScored(model)) {
          questionFeedbackManager.setShowQuestionFeedback(lessonElementId, false);
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, false);
          state.questionBehavior.setCorrectAnswer(state, model, isPracticeRunning);
          state.turnOnAnswerShowing();
          if (state.isSubmitted) {
            await responseManager.scoreResponse(model, lessonElementId);
          }
          questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        }
      } else if (state.questionBehavior && model.validation && !toggle) {
        if (state.questionBehavior.isAutoScored(model)) {
          questionFeedbackManager.setShowQuestionFeedback(lessonElementId, false);
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, false);
          state.questionBehavior.resetStudentAnswer(state, isPracticeRunning);
          state.turnOffAnswerShowing();
          if (state.isSubmitted) {
            await responseManager.scoreResponse(model, lessonElementId);
          }
          questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        }
      }
      if (mode === LessonMode.SCORING) {
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, (!toggle && state.isSubmitted));
      }
      if (isPracticeRunning) {
        questionFeedbackManager.endPractice(lessonElementId);
        this.doFeedbackLogic(lessonElementId, model, mode, state);
        this.doDependencyLogic(lessonElementId);
      }
      questionFeedbackManager.setShowAnswers(lessonElementId, toggle);
    }
    if (toggle) {
      questionFeedbackManager.addButtonUsage('showAnswer');
      // Announce that answers are being shown
      accessibilityService.announceShowAnswers();
    }
  }

  handleSubmitResponse = async (lessonElementId) => {
    const mode = lessonManager.playerMode;
    const model = lessonManager.getSlideModel(lessonElementId);
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);

    const shouldSubmit = ((mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION) &&
      ((model.unscored || model.type === ContentType.ALCHEMIE_TEST_ITEM.type) ||
      (!lessonElementState.isScored &&
        (UtilsService.isNullOrEmptyOrUndefined(questionFeedbackManager.questionFeedbackMode.scoringOption) ||
         questionFeedbackManager.questionFeedbackMode.scoringOption === ScoringOption.FIRST)) ||
      (!UtilsService.isNullOrEmptyOrUndefined(questionFeedbackManager.questionFeedbackMode.scoringOption) &&
       questionFeedbackManager.questionFeedbackMode.scoringOption !== ScoringOption.FIRST)) &&
      !model.isGame);

    if (shouldSubmit) {
      const lessonElement = lessonManager.getLessonElement(lessonElementId);
      const isScoreNow = true;
      const activityId = studentActivityManager.studentActivityId;
      const viewedTime = timeManager.getViewTime(lessonElementId);
      const noSubmit = false;

      if (model.isThirdPartyApiScoring) {
        let scoreIt = true;
        if (!model.validation) {
          scoreIt = await this.getFeedbackValidation(lessonElementId);
        }
        if (scoreIt) {
          await this.scoreThirdPartyItem(lessonElementId, lessonElementState, lessonElementState.currentResponseAnswer, model);
        }
      }

      const scoringOption = model.type === ContentType.ALCHEMIE_TEST_ITEM.type ? ScoringOption.LAST : questionFeedbackManager.questionFeedbackMode.scoringOption;

      const serverResponse = responseManager.getUpdatedServerResponse(
        lessonElementState,
        activityId,
        viewedTime,
        isScoreNow,
        lessonElement.entityId,
        noSubmit,
        scoringOption,
        { model }
      );

      const success = await this.submitResponses([serverResponse]);
      if (success) {
        const currentUiState = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
        const { currentAttemptCount } = currentUiState;
        questionFeedbackManager.setCurrentAttemptCount(lessonElementId, currentAttemptCount + 1);
        return true;
      }
      questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
      return false;
    }

    if (model.isThirdPartyApiScoring) {
      let scoreIt = true;
      if (!model.validation) {
        scoreIt = await this.getFeedbackValidation(lessonElementId);
      }
      if (scoreIt) {
        await this.scoreThirdPartyItem(lessonElementId, lessonElementState, lessonElementState.currentResponseAnswer, model);
      }
    }
    questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
    responseManager.setPreviewResponseSubmitted(lessonElementId, shouldSubmit || (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW));
    responseManager.setReadOnly(lessonElementId, true);
    const currentUiState = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    const { currentAttemptCount } = currentUiState;
    questionFeedbackManager.setCurrentAttemptCount(lessonElementId, currentAttemptCount + 1);
    return true;
  }

  scoreThirdPartyItem = async (lessonElementId, lessonElementState, currentResponseAnswer, model) => {
    if (model && model.isThirdPartyApiScoring && model.validation && model.validation.answers) {
      const answers = [];
      model.validation.answers.forEach((answer) => {
        answers.push(answer.goal);
      });
      const config = model.config;
      let alchemieUrl = null;
      if (model.type === ContentType.ALCHEMIE_TEST_ITEM.type) {
        const parentModel = lessonManager.getSlideModel(model.parentActivityElementId);
        alchemieUrl = parentModel.alchemieUrl;
      } else {
        alchemieUrl = model.alchemieUrl;
      }

      const isCorrect = await Auth.fetch('https://lti13.alchem.ie/scorer', {
        method: 'POST',
        body: {
          url: alchemieUrl,
          response: currentResponseAnswer.goal,
          goal: answers,
          params: config
        }
      });

      currentResponseAnswer.isCorrect = isCorrect.result;
    } else {
      currentResponseAnswer.isCorrect = false;
    }
  }

  getScoreFeedback = async (lessonElementId, lessonElementState, currentResponseAnswer, model) => {
    const currentUiState = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    const { currentAttemptCount } = currentUiState;
    if (!model.isGame) {
      const serverResponse = responseManager.getFeedbackResponse(
        lessonElementId, currentResponseAnswer, model
      );

      const success = await this.fetchScoreFeedback([serverResponse], null, null, currentAttemptCount > 1);
      if (success) {
        return true;
      }
      questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
      return false;
    }
    questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
    responseManager.setPreviewResponseSubmitted(lessonElementId, true);
    responseManager.setReadOnly(lessonElementId, true);

    questionFeedbackManager.setCurrentAttemptCount(lessonElementId, currentAttemptCount + 1);
    return true;
  }

  doDependencyLogic = (lessonElementId) => {
    const sequential = responseManager.sequentialItemDependency;
    const nonSequential = (!responseManager.sequentialItemDependency && responseManager.dependencyFlowMap.size > 0);

    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    const currentUiMode = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    const { currentAttemptCount } = currentUiMode;
    const { practiceAttempts } = questionFeedbackManager.questionFeedbackMode;
    const attemptsLeft = currentAttemptCount < practiceAttempts;
    const isCorrect = lessonElementState.maxScore === lessonElementState.submittedItemScore;

    const doNonSequential = (nonSequential && (isCorrect || !attemptsLeft ||
      !questionFeedbackManager.isPractice));

    if (lessonElementState.dependentList.length > 0) {
      if (sequential || doNonSequential) {
        lessonElementState.dependentList.forEach((id) => {
          const dependentState = responseManager.getLessonElementState(id);
          if (dependentState) {
            dependentState.removeController(lessonElementId);
          }
        });
        lessonElementState.removeAllDependents();
      }
    }
  }

  saveResponses = async (serverResponses, forceSubmit) => {
    
    if (responseManager.isSubmitting) {
      return false;
    }

    responseManager.setIsSubmitting(true);

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/submitResponses`, {
        method: 'POST',
        body: { responses: JSON.stringify(serverResponses) }
      });
      if ((result.status === 'SUCCESS')) {
        const { submittedResponses } = result;

        submittedResponses.forEach((submittedResponse) => {
          const { lessonElementId } = submittedResponse;
          responseManager.setResponseId(submittedResponse.id, lessonElementId);
          responseManager.setCurrentResponseSaved(lessonElementId, forceSubmit, submittedResponse);
        });
        responseManager.setIsSubmitting(false);
        return true;
      }
      if (result.status === 500 || result.status === 503 || result.status === 504) {
        await this.fireSaveErrorAlert();
      }
      responseManager.setIsSubmitting(false);
      return false;
    } catch (e) {
      responseManager.setIsSubmitting(false);
      await this.fireSaveErrorAlert();
      console.error(e);
      return false;
    }
  }

  setMarkedForReview = (lessonElementId, toogle) => {
    responseManager.setMarkedForReview(lessonElementId, toogle);
  }

  submitResponses = async (serverResponses) => {
    if (responseManager.isSubmitting) {
      return false;
    }
    responseManager.setIsSubmitting(true);

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/submitResponses`, {
        method: 'POST',
        body: { responses: JSON.stringify(serverResponses) }
      });
      if (result.status === 'SUCCESS') {
        const { submittedResponses, scoringFeedback } = result;
        submittedResponses.forEach((submittedResponse) => {
          const { id, lessonElementId } = submittedResponse;
          const feedback = scoringFeedback.find((element) => element.activityResponseId === id);

          const model = lessonManager.getSlideModel(lessonElementId);

          responseManager.setResponseId(submittedResponse.id, lessonElementId);
          if (submittedResponse.validation) {
            const model = lessonManager.getSlideModel(lessonElementId);
            if (!model.validation) {
              lessonManager.setModelValidation(lessonElementId, JSON.parse(submittedResponse.validation));
            }
          }
          responseManager.setCurrentResponseSubmitted(lessonElementId, result.scoreState, feedback, model);

          responseManager.setReadOnly(lessonElementId, true);
        });
        responseManager.setIsSubmitting(false);
        return true;
      }
      await this.fireSaveErrorAlert();
      console.info(result.status);
      console.info(result.statusMessage);
      responseManager.setIsSubmitting(false);
      return false;
    } catch (e) {
      if (UtilsService.isConnectionError(e)) {
        await this.fireSaveErrorAlert();
      } else if (UtilsService.isLockedError(e)) {
        await this.fireSaveErrorAlert(t('lockError'));
      } else if (UtilsService.isReviewError(e)) {
        await this.fireSaveErrorAlert(t('alreadySubmittedError'));
      } else if (UtilsService.isInternalError(e)) {
        const message = e.message || t('errorGeneric');
        await this.fireSaveErrorAlert(`${message} ${t('generalSaveError')}`);
      } else if (UtilsService.isSystemError(e)) {
        await this.fireSaveErrorAlert();
      } else {
        await this.fireSaveErrorAlert();
      }

      console.error(e);
      responseManager.setIsSubmitting(false);
      return false;
    }
  }

  getFeedbackValidation = async (lessonElementId) => {
    if (responseManager.isSubmitting) {
      return false;
    }
    responseManager.setIsSubmitting(true);

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/getFeedbackValidation?lessonElementId=${lessonElementId}`, {
        method: 'GET',
      });
      if (result.status === 'SUCCESS') {
        const { validation } = result;

        const model = lessonManager.getSlideModel(lessonElementId);
        if (!model.validation) {
          lessonManager.setModelValidation(lessonElementId, JSON.parse(validation));
        }
        responseManager.setIsSubmitting(false);
        return true;
      }
      await this.fireSaveErrorAlert();
      console.info(result.status);
      console.info(result.statusMessage);
      responseManager.setIsSubmitting(false);
      return false;
    } catch (e) {
      if (UtilsService.isConnectionError(e)) {
        await this.fireSaveErrorAlert();
      } else if (UtilsService.isLockedError(e)) {
        await this.fireSaveErrorAlert(t('lockError'));
      } else if (UtilsService.isReviewError(e)) {
        await this.fireSaveErrorAlert(t('alreadySubmittedError'));
      } else if (UtilsService.isInternalError(e)) {
        const message = e.message || t('errorGeneric');
        await this.fireSaveErrorAlert(`${message} ${t('generalSaveError')}`);
      } else if (UtilsService.isSystemError(e)) {
        await this.fireSaveErrorAlert();
      } else {
        await this.fireSaveErrorAlert();
      }

      console.error(e);
      responseManager.setIsSubmitting(false);
      return false;
    }
  }

  setFeedback = (feedback, lessonElementId) => {
    console.log('setFeedback', feedback, lessonElementId);
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    lessonElementState.setFeedback(feedback, lessonManager.playerMode);
  }

  fetchScoreFeedback = async (feedbackResponses, activityId, userId, isPractice) => {
    if (responseManager.isSubmitting) {
      return false;
    }
    responseManager.setIsSubmitting(true);

    const mode = lessonManager.playerMode;
    let url;
    if (mode === LessonMode.ITEM_PREVIEW) {
      url = `${Auth.ecms}/api/getItemPreviewFeedbackForResponses`;
    } else {
      url = `${Auth.ecms}/api/getFeedbackForResponses`;
    }

    try {
      const result = await Auth.fetch(url, {
        method: 'POST',
        body: { responses: JSON.stringify(feedbackResponses), activityId, userId, isPractice }
      });
      if (result.status === 'SUCCESS') {
        const { scoringFeedback } = result;
        scoringFeedback.forEach((feedback) => {
         
          const { lessonElementId } = feedback;

          if (feedback !== null && feedback !== undefined) {
            if (feedback.validation) {
              const model = lessonManager.getSlideModel(lessonElementId);
              if (!model.validation) {
                lessonManager.setModelValidation(lessonElementId, JSON.parse(feedback.validation));
              }
            }

            this.setFeedback(feedback, lessonElementId);
          }

          responseManager.setReadOnly(lessonElementId, true);
        });
        responseManager.setIsSubmitting(false);
        return true;
      }
      await this.fireSaveErrorAlert();
      console.info(result.status);
      console.info(result.statusMessage);
      responseManager.setIsSubmitting(false);
      return false;
    } catch (e) {
      if (UtilsService.isConnectionError(e)) {
        await this.fireSaveErrorAlert();
      } else if (UtilsService.isLockedError(e)) {
        await this.fireSaveErrorAlert(t('lockError'));
      } else if (UtilsService.isReviewError(e)) {
        await this.fireSaveErrorAlert(t('alreadySubmittedError'));
      } else if (UtilsService.isInternalError(e)) {
        const message = e.message || t('errorGeneric');
        await this.fireSaveErrorAlert(`${message} ${t('generalSaveError')}`);
      } else if (UtilsService.isSystemError(e)) {
        await this.fireSaveErrorAlert();
      } else {
        await this.fireSaveErrorAlert();
      }

      console.error(e);
      responseManager.setIsSubmitting(false);
      return false;
    }
  }

  submitLessonForGrade = async () => {
    const { activityId } = studentActivityManager;
    const total = responseManager.totalTestItemResponses;
    const submittedCount = responseManager.numberSubmittedResponses;
    const incompleteResponseCount = (total - (submittedCount));

    let result;
    if (incompleteResponseCount > 0) {
      result = await confirmDialog({
        title: `${UtilsService.submitCheck()}`,
        text: `${UtilsService.notCompleteWarning()}`,
      });
    } else {
      result = await confirmDialog({
        title: `${UtilsService.submitCheck()}`,
        text: `${UtilsService.cannotBe()}`,
      });
    }
    if (result.isConfirmed) {
      await this.submitActivityInstance(activityId);
    }
  }

  saveSingleResponse = async (lessonElementId) => {
    const mode = lessonManager.playerMode;

    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION) {
      const activityId = studentActivityManager.studentActivityId;
      const lessonElementState = responseManager.getLessonElementState(lessonElementId);
      const lessonElement = lessonManager.getLessonElement(lessonElementId);
      const model = lessonManager.getSlideModel(lessonElementId);
      const { noSubmit } = questionFeedbackManager;
      let forceSubmit = false;

      if (noSubmit && lessonElementState && lessonElementState.questionBehavior) {
        const answered = lessonElementState.questionBehavior.checkForValidResponse(lessonElementState.currentResponseAnswer, model);
        if (answered && model.isTestItem && !lessonElementState.questionBehavior.requiresExplicitSubmitOnNavigation()) {
          forceSubmit = true;
        }
      }

      if (model.survey && lessonElementState && lessonElementState.questionBehavior) {
        const answered = lessonElementState.questionBehavior.checkForValidResponse(lessonElementState.currentResponseAnswer, model);
        if (answered && model.isTestItem && model.survey) {
          forceSubmit = true;
        }
      }

      if (lessonElementState && !model.isGame) {
        const viewTime = timeManager.getViewTime(lessonElementId);
        const { noSubmit } = questionFeedbackManager;
        const scoringOption = model.type === ContentType.ALCHEMIE_TEST_ITEM.type ? ScoringOption.LAST : questionFeedbackManager.questionFeedbackMode.scoringOption;
        const serverResponse = responseManager.getUpdatedServerResponse(
          lessonElementState,
          activityId,
          viewTime,
          false,
          lessonElement.entityId,
          noSubmit,
          scoringOption,
          { model }
        );
        const responses = [serverResponse];
        await this.saveResponses(responses, forceSubmit);
      }
    }
  }

  saveAllResponses = async () => {
    const questionList = lessonManager.elementsThatHaveState;
    const activityId = studentActivityManager.studentActivityId;
    const mode = lessonManager.playerMode;
    const responses = [];
    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION) {
      for (let i = 0; i < questionList.length; ++i) {
        const model = questionList[i];
        if (!model.isGame) {
          const lessonElementState = responseManager.getLessonElementState(model.lessonElementId);
          const lessonElement = lessonManager.getLessonElement(model.lessonElementId);
          if (lessonElementState) {
            const viewTime = timeManager.getViewTime(model.lessonElementId);
            const { noSubmit } = questionFeedbackManager;
            const scoringOption = model.type === ContentType.ALCHEMIE_TEST_ITEM.type ? ScoringOption.LAST : questionFeedbackManager.questionFeedbackMode.scoringOption;
            const serverResponse = responseManager.getUpdatedServerResponse(
              lessonElementState,
              activityId,
              viewTime,
              false,
              lessonElement.entityId,
              noSubmit,
              scoringOption,
              { model }
            );
            responses.push(serverResponse);
          }
        }
      }
      if (responses.length >= 0) {
        await this.saveResponses(responses);
      }
    }
  }

  fetchTeacherComments = async (lessonElementId) => {
    if (!lessonElementId) {
      return;
    }
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);

    if (!lessonElementState) {
      return;
    }

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/viewComment?entityId=${lessonElementState.responseId}`, {
        method: 'GET'
      });
      if (result.status === 'SUCCESS') {
        responseManager.setResponseComment(lessonElementId, result.comment);
      } else {
        console.info(result.status);
        console.info(result.statusMessage);
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  fetchAllTeacherComments = async () => {
    const slides = lessonManager.getFilteredSlides();
    const responseIds = [];

    slides.forEach((lessonElementId) => {
      const lessonElementState = responseManager.getLessonElementState(lessonElementId);
      if (lessonElementState && lessonElementState.responseId) {
        const newId = `'${lessonElementState.responseId}'`;
        responseIds.push(newId);
      }
    });

    if (responseIds.length === 0) {
      return;
    }

    const idString = responseIds.toString();

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/viewComments?entityIds=${idString}`, {
        method: 'GET'
      });
      if (result.status === 'SUCCESS') {
        if (result.comments && result.comments.discussions && result.comments.discussions.length > 0) {
          responseManager.setResponseComments(result.comments.discussions);
        }
      } else {
        console.info(result.status);
        console.info(result.statusMessage);
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  doScoringModeStartUp = async (activityId, studentViewId, lessonElementIdRequested, isNext) => {
    lessonManager.lessonElementIdRequested = lessonElementIdRequested;
    lessonManager.createSlideLabels();
    if (isNext) {
      lessonManager.setStartWithOnlyManual();
    }

    if (studentActivityManager.students.length === 0) {
      await studentActivityService.fetchActivityUsers(activityId, studentViewId);
    }
    const student = studentActivityManager.currentStudent;
    studentActivityManager.setStudentName(student);
    studentActivityManager.setStudentId(student.id);
    lessonManager.manualSlideList = [];
    lessonManager.doNotScoreSlideList = [];

    await this.fetchResponses(activityId, studentViewId);
    await this.fetchAllTeacherComments();
    scoringService.initTempScores();

    if (!UtilsService.isNullOrEmpty(lessonElementIdRequested)) {
      const index = lessonManager.slideList.indexOf(lessonElementIdRequested);
      if (index > 0 && index < lessonManager.slideList.length) {
        lessonManager.setCurrentSlideIndex(index);
      }
    }
  }

  processReturnedResponses = async (model, responses, activityId, mode, userId) => {
    const type = (model.type) ? model.type : null;
    const behavior = createQuestionBehavior(type);
    const { lessonElementId } = model;
    const response = responses[model.id];

    if (response) {
      const mustHaveUserInputMap = questionService.mustHaveUserInputMap(model);

      if (response.validation && (!model.validation) &&
        ((mode === LessonMode.ACTIVITY_MODE) ||
        (mode === LessonMode.TEACHER_PRESENTATION) ||
        (mode === LessonMode.SCORING) ||
        (mode === LessonMode.REVIEW))) {
        lessonManager.setModelValidation(lessonElementId, JSON.parse(response.validation));
      }
      let responseAnswer = await behavior.getInitializedResponse(lessonElementId, model, activityId);

      if (!UtilsService.isNullOrEmpty(response.content) && response.content !== '{}') {
        responseAnswer = JSON.parse(response.content);
      }
      let toolPersistence = {};
      if (!UtilsService.isNullOrEmpty(response.toolPersistence) && response.toolPersistence !== '{}') {
        try {
          toolPersistence = JSON.parse(response.toolPersistence);
        } catch (e) {
          // It's not JSON, leave it []
        }
      }

      const isScored = (!UtilsService.isNullOrEmpty(response.scoreState) &&
        !(response.scoreState === ScoreState.NOT_SCORED) && !model?.unscored);

      responseManager.createLessonElementState(
        activityId, response.id, response.maxScore, response.lessonElementId,
        model.id, responseAnswer, response.viewedTime,
        false, toolPersistence, true, response.locked,
        isScored, response.scoreState,
        response.note, response.markedForReview, response.subscore, behavior,
        true, model.isTestItem, response.submittedItemScore, response.submittedMaxScore, model.survey, model.unscored, model.offlineGrading, response.bedrockUsageId);

      const state = responseManager.getLessonElementState(lessonElementId);
      if (mustHaveUserInputMap) {
        state.currentResponseAnswer.prompts = responseAnswer.prompts;
        state.currentResponseAnswer.userInputMap = questionService.updateUserInputMap({
          model,
          lessonElementState: {
            ...state,
            prompts: responseAnswer.prompts
          }
        });
        responseAnswer.userInputMap = questionService.updateUserInputMap({ model, responseAnswer });
      }

      if (questionFeedbackManager.noSubmit && !behavior.requiresExplicitSubmitOnNavigation() && (behavior.checkForValidResponse(state.currentResponseAnswer, model) ||
        (mustHaveUserInputMap && responseAnswer?.prompts?.some?.((userInput) => !!userInput.text)))
      ) {
        state.setSubmitted(true);
      }

      const isCorrect = behavior.isQuestionCorrect(state, lessonElementId);
      const isAutoScored = behavior.isAutoScored(model);
      if (!isScored && model.isTestItem && !model.unscored && !isAutoScored) {
        responseManager.addNotScoredList(lessonElementId);
      }

      const isAnsweredSurvey = questionService.isAnsweredSurveyQuestion({
        lessonElementId,
        lessonElementState: state,
        model
      });

      if ((state.isSubmitted && model.isTestItem) || isAnsweredSurvey) {
        responseManager.addResponseSubmittedList(lessonElementId);
      }

      if (state.isSubmitted && isScored && model.type !== ContentType.LIKERT.type) {
        switch (isCorrect) {
          case QuestionFeedbackState.CORRECT: {
            responseManager.addCorrectList(lessonElementId);
            break;
          }
          case QuestionFeedbackState.INCORRECT: {
            responseManager.addIncorrectList(lessonElementId);
            break;
          }
          case QuestionFeedbackState.PARTIAL_CORRECT: {
            responseManager.addPartiallyCorrectList(lessonElementId);
            break;
          }
          default: {
            break;
          }
        }
      } else if (state.isSubmitted && !isScored && !model.unscored && !isAutoScored) {
        responseManager.addNotScoredList(lessonElementId);
      }

      if (state.isSubmitted) {
        state.setCachedCorrectState(isCorrect);
      }

      if (response.engagementData) {
        responseManager.setEngagementData(response.lessonElementId, response.engagementData);
      }

      if (response.viewedTime && (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION)) {
        timeManager.contentItemViewedTime.set(lessonElementId, response.viewedTime);
      }

      if (questionFeedbackManager.isPractice && response.locked) {
        questionFeedbackManager.setCurrentAttemptCount(response.lessonElementId, -1);
        // this person has submitted, go to finished
      }

      if (response.locked || mode === LessonMode.REVIEW || mode === LessonMode.SCORING) {
        responseManager.setReadOnly(lessonElementId, true);
        const serverResponse = responseManager.getFeedbackResponse(
          lessonElementId, state.currentResponseAnswer, model
        );

        if (isAutoScored && response.locked) {
          await this.fetchScoreFeedback([serverResponse], activityId, userId);
        }

        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, state.isSubmitted);

        state.setCachedResponse(UtilsService.safeMobxClone(state.currentResponseAnswer), state.submittedItemScore);

        if (mode === LessonMode.SCORING) {
          if ((model.unscored) && model.isTestItem) {
            lessonManager.addDoNotScoreSlide(response.lessonElementId);
          } else if ((!isAutoScored && model.isTestItem) || (model.survey && model.isTestItem)) {
            lessonManager.addManuallyScoredSlide(response.lessonElementId);
          }
          lessonManager.setStartWithOnlyManual();
        }

        questionFeedbackManager.setSubmitVisibleState(lessonElementId, false);
        questionFeedbackManager.setTryAgainVisibleState(lessonElementId, false);
        questionFeedbackManager.setShowAnswers(lessonElementId, false);
        if (isAutoScored) {
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
          questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, true);
        }
      }
    }
  }

  fetchClassEngagementSummary = async (activityId) => {
    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/viewClassroomActivityElementEngagement?activityId=${activityId}`, {
        method: 'GET'
      });
      if (result.status === 'SUCCESS') {
        for (const property in result.data) {
          responseManager.setClassEngagementReport(result.data[property]);
        }
      } else {
        console.info(result.status);
        console.info(result.statusMessage);
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  fetchStudentReviewEngagementSummary = async (activityId, studentId) => {
    let studentParam = '';
    if (!UtilsService.isNullOrEmpty(studentId)) {
      studentParam = `&studentId=${studentId}`;
    }
    const finalParams = `?activityId=${activityId}${studentParam}`;
    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/viewActivityEngagement${finalParams}`, {
        method: 'GET'
      });
      if (result.status === 'SUCCESS') {
        responseManager.setStudentReviewEngagementReport(result.data);
      } else {
        console.info(result.status);
        console.info(result.statusMessage);
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  fetchResponses = async (activityId, userId) => {
    const assignmentData = await this.fetchActivityStudentResponsesAndScores(activityId);
    if (assignmentData === null) {
      return false;
    }
    const responses = assignmentData.responsesScores;
    const questionList = lessonManager.elementsThatHaveState;
    const mode = lessonManager.playerMode;

    if (mode === LessonMode.SCORING) {
      studentActivityManager.setStudentActivityId(activityId);
      if (toolbarManager.isNotepadOpen) {
        await studentActivityService.fetchStudentNotepadNote(activityId);
      }
    }
    // don't use forEach function here or await will not work on each call
    for (let x = 0; x < questionList.length; ++x) {
      await this.processReturnedResponses(questionList[x], responses, activityId, mode, userId);
    }
    return true;
  }

  isAnswerCorrect = (lessonElementId, answerId, { lessonElementState, model } = {}) => {
    model = model || lessonManager.getSlideModel(lessonElementId);
    const wrongMode = (lessonManager.playerMode === LessonMode.SCORING || lessonManager.playerMode === LessonMode.REVIEW);
    const { isPractice, practiceAttempts } = questionFeedbackManager.questionFeedbackMode;
    const uiTracker = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    let isPracticeRunning = false;

    if (uiTracker && (uiTracker.currentAttemptCount < practiceAttempts) && isPractice && !wrongMode) {
      isPracticeRunning = true;
    }
    return responseManager.isAnswerCorrect(lessonElementId, answerId, model, isPracticeRunning, { lessonElementState });
  }

  isAutoScored = (lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    return responseManager.isAutoScored(lessonElementState, model);
  }

  fetchLessonElementResponseContent = async (activityId, lessonElementId) => {
    // Determine the appropriate userId based on context
    // For students, use their own ID; for teachers viewing student work, use the student's ID, then fallback to the teacher's ID for presentation mode
    const userId = userManager.isStudent
      ? userManager.userId
      : (studentActivityManager.studentId || userManager.userId);

    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/viewActivityResponseContent?userId=${userId}&activityId=${activityId}&entityId=${lessonElementId}`, {
        method: 'GET'
      });
      if (result.status === 'SUCCESS') {
        return result.contentItems;
      } else {
        if (result.status === 500 || result.status === 503 || result.status === 504) {
          // await this.fireSaveErrorAlert();
          // TODO: Create a new alert for this
        }
        console.info(result.status);
        console.info(result.statusMessage);
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  uploadAndAttachFileResource = async (file, activityId, contentObjectId, lessonElementId, entityTypeId = 'content_item', {
    mimeMainType
  } = {}) => {
    // Upload the file
    // const uploadFileId = `files_${contentObjectId}`;
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (mimeMainType) {
        formData.append('mimeMainType', mimeMainType);
      }

      const uploadResult = await Auth.fetch(`${Auth.ecms}/api/uploadFileResource?entityId=${contentObjectId}&entityTypeId=${entityTypeId}`, {
        method: 'POST',
        body: formData
      });
      if (uploadResult.status !== 'SUCCESS' && uploadResult.status !== 'WARNING') {
        if (uploadResult.status === 500 || uploadResult.status === 503 || uploadResult.status === 504) {
          // await this.fireSaveErrorAlert();
          // TODO: Create a new alert for this
        }
        console.info(uploadResult.status);
        console.info(uploadResult.statusMessage);
        return null;
      }
      // Otherwise the file was uploaded successfully and we need to attach it
      const { contentItemId } = uploadResult;
      const attachResult = await Auth.fetch(`${Auth.ecms}/api/attachActivityResponseContent`, {
        method: 'POST',
        body: {
          activityId,
          entityId: lessonElementId,
          contentItemId
        }
      });
      if (attachResult.status !== 'SUCCESS') {
        if (attachResult.status === 500 || attachResult.status === 503 || attachResult.status === 504) {
          // await this.fireSaveErrorAlert();
          // TODO: Create a new alert for this
        }
        console.info(attachResult.status);
        console.info(attachResult.statusMessage);
        return null;
      }
      return contentItemId;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  updateContentItemName = async (contentItemId, name) => {
    // ContentItemId is the id returned from viewActivityResponseContent
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/updateContentItemName`, {
        method: 'POST',
        body: {
          id: contentItemId,
          name
        }
      });
      if (response.status === 'SUCCESS') {
        return true;
      } else {
        if (response.status === 500 || response.status === 503 || response.status === 504) {
          // await this.fireSaveErrorAlert();
          // TODO: Create a new alert for this
        }
        console.info(response.status);
        console.info(response.statusMessage);
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  removeActivityResponseContent = async (activityId, lessonElementId, contentItemId) => {
    // ContentItemId is the id returned from viewActivityResponseContent
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/removeActivityResponseContent`, {
        method: 'POST',
        body: {
          activityId,
          entityId: lessonElementId,
          contentItemId
        }
      });
      if (response.status === 'SUCCESS') {
        return true;
      } else {
        if (response.status === 500 || response.status === 503 || response.status === 504) {
          // await this.fireSaveErrorAlert();
          // TODO: Create a new alert for this
        }
        console.info(response.status);
        console.info(response.statusMessage);
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  fetchActivityStudentResponsesAndScores = async (activityId) => {
    if (studentActivityManager.studentId) {
      // Requesting a student's data.
      try {
        const result = await Auth.fetch(`${Auth.ecms}/api/getActivityStudentResponsesAndScores?activityId=${activityId}&studentId=${studentActivityManager.studentId}`, {
          method: 'GET'
        });
        if (result.status === 'SUCCESS') {
          return result.responsesAndScores;
        } else {
          if (result.status === 500 || result.status === 503 || result.status === 504) {
            await this.fireSaveErrorAlert();
          }
          // console.info(result.status);
          // console.info(result.statusMessage);
          return null;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    } else {
      // User is the student; get their data.
      try {
        const result = await Auth.fetch(`${Auth.ecms}/api/getActivityResponsesAndScores?activityId=${activityId}`, {
          method: 'GET'
        });
        if (result.status === 'SUCCESS') {
          return result.responsesAndScores;
        } else {
          if (result.status === 500 || result.status === 503 || result.status === 504) {
            await this.fireSaveErrorAlert();
          }
          console.info(result.status);
          console.info(result.statusMessage);
          return null;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    }
  }

  submitActivityInstance = async (activityId) => {
    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/submitActivityInstance`, {
        method: 'POST',
        body: { activityId },
      });
      if (result.status === 'SUCCESS') {
        await UtilsService.exitPlayer();
      } else {
        if (result.status === 500 || result.status === 503 || result.status === 504) {
          await this.fireSaveErrorAlert();
        }
        console.info(result.status);
        console.info(result.statusMessage);
      }
    } catch (e) {
      if (UtilsService.isConnectionError(e)) {
        await this.fireSaveErrorAlert();
      } else if (UtilsService.isLockedError(e)) {
        await this.fireSaveErrorAlert(t('lockError'));
      } else if (UtilsService.isReviewError(e)) {
        await this.fireSaveErrorAlert(t('alreadySubmittedError'));
      } else if (UtilsService.isInternalError(e)) {
        const message = e.message || t('errorGeneric');
        await this.fireSaveErrorAlert(`${message} ${t('generalSaveError')}`);
      } else if (UtilsService.isSystemError(e)) {
        await this.fireSaveErrorAlert();
      }
      console.error(e);
    }
  }

  fireSaveErrorAlert = async (message, title) => {
    await confirmDialog({
      title: (title) || `${UtilsService.errorAlert()}`,
      text: (message) || `${UtilsService.saveErrorMessage()}`,
      confirmButtonText: `${t('close')}`
    });
  }

  clearFeedback = (lessonElementId) => {
    questionFeedbackManager.setShowAnswerFeedback(lessonElementId, false);
    questionFeedbackManager.setShowQuestionFeedback(lessonElementId, false);
    questionFeedbackManager.setShowAnswers(lessonElementId, false);
  }

  textChangeHandler = (data, lessonElementId, { dataId = '', lessonElementState, oldDataId = '', ...rest } = {}) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    responseManager.setResponseData(lessonElementId, data, model, { dataId, lessonElementState, oldDataId, ...rest });
  }

  teacherNotesChanged = (notes, lessonElementId) => {
    responseManager.updateTeacherNote(notes, lessonElementId);
  }

  responseChangeHandler = async (data, lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    await responseManager.setResponseData(lessonElementId, data, model);
  }

  holdInteractiveMessage = (lessonElementId, data) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    if (!state || lessonManager.isReadOnly || state.readOnly || state.isSubmitted) {
      return false;
    }
    return state.questionBehavior?.holdInteractiveMessage?.(lessonElementId, data) ?? false;
  }

  flushBufferedInteractiveMessage = async (lessonElementId) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    const model = lessonManager.getSlideModel(lessonElementId);
    if (!state?.questionBehavior?.flushInteractiveMessage) {
      return false;
    }
    return state.questionBehavior.flushInteractiveMessage(
      lessonElementId,
      state.currentResponseAnswer,
      model
    );
  }

  clearInteractiveMessageSession = (lessonElementId) => {
    const state = responseManager.getLessonElementState(lessonElementId);
    state?.questionBehavior?.clearInteractiveMessageSession?.(lessonElementId);
  }

  responseRemoveHandler = async (data, lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    await responseManager.removeResponseData(lessonElementId, data, model);
  }

  getResponseModel = (lessonElementId) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    return lessonElementState.currentResponseAnswer;
  }

  isMarkedForReview = (lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    if (model.isActivity) {
      const parentLessonElement = lessonManager.getLessonElement(lessonElementId);
      if (parentLessonElement.lessonElementIds && parentLessonElement.lessonElementIds.length > 0) {
        for (let i = 0; i < parentLessonElement.lessonElementIds.length; i++) {
          const elementId = parentLessonElement.lessonElementIds[i];
          if (responseManager.markedForReviewList.includes(elementId)) {
            return true;
          }
        }
      }
      return false;
    }
    return responseManager.markedForReviewList.includes(lessonElementId);
  }

  getShowAnswerText = (lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    const behavior = responseManager.getQuestionBehavior(lessonElementId);

    if (behavior && model?.validation) {
      return behavior.getCorrectAnswersText(model);
    } else {
      return '';
    }
  }

  getQuestionFeedbackText = (lessonElementId) => {
    const mode = lessonManager.playerMode;
    const model = lessonManager.getSlideModel(lessonElementId);
    const { hasFeedback } = questionFeedbackManager;

    const behavior = responseManager.getQuestionBehavior(lessonElementId);
    if (!behavior) {
      return '';
    }

    const lessonElementState = responseManager.getLessonElementState(lessonElementId);

    const isQuestionCorrect = responseManager.isQuestionCorrect(lessonElementId);

    let modelCorrectFeedback = '';
    let modelIncorrectFeedback = '';
    let modelCorrectFeedbackBody = '';
    let modelIncorrectFeedbackBody = '';

    if (model?.validation?.feedback?.level === 'answer') {
      // NOTE: `userAnswer.isCorrect` (when true) is what the user marked as the 'correct' answer.
      // This does not necessarily mean it is the actual correct answer.
      const chosenUserAnswer = (lessonElementState?.currentResponseAnswer?.answers?.find((userAnswer) => {
        const userMarkedAnswerAsCorrect = userAnswer?.isCorrect;
        return userMarkedAnswerAsCorrect;
      }));
      const chosenUserAnswerFeedback = chosenUserAnswer ? model?.validation?.feedback?.answers?.[chosenUserAnswer?.id] : undefined;

      modelCorrectFeedback = chosenUserAnswerFeedback?.heading;
      modelIncorrectFeedback = chosenUserAnswerFeedback?.heading;
      modelCorrectFeedbackBody = chosenUserAnswerFeedback?.body;
      modelIncorrectFeedbackBody = chosenUserAnswerFeedback?.body;
    } else if (model?.validation?.feedback) {
      modelCorrectFeedback = model.validation.feedback.correctFeedback;
      modelIncorrectFeedback = model.validation.feedback.incorrectFeedback;
      modelCorrectFeedbackBody = model.validation.feedback.correctFeedbackBody;
      modelIncorrectFeedbackBody = model.validation.feedback.incorrectFeedbackBody;
    }

    let finalCorrect = questionFeedbackManager.correctFeedback;
    let finalIncorrect = questionFeedbackManager.incorrectFeedback;

    if (finalCorrect !== UtilsService.correct()) {
      finalCorrect = UtilsService.correct();
    }

    if (finalIncorrect !== UtilsService.incorrect()) {
      finalIncorrect = UtilsService.incorrect();
    }

    const correctFeedback = modelCorrectFeedback || finalCorrect;
    const incorrectFeedback = modelIncorrectFeedback || finalIncorrect;

    let result = null;

    if (mode === LessonMode.SCORING && lessonElementState.questionBehavior.isAutoScored(model)) {
      if (isQuestionCorrect === QuestionFeedbackState.CORRECT) {
        result = '<div class=\'question-feedback-header\'>';
        result += correctFeedback;
        result += '</div>';
      } else if (isQuestionCorrect === QuestionFeedbackState.PARTIAL_CORRECT) {
        result = '<div class=\'question-feedback-header\'>';
        result += incorrectFeedback;
        result += '</div>';
      } else {
        result = '<div class=\'question-feedback-header\'>';
        result += incorrectFeedback;
      }
    }

    if (result !== null) {
      return result;
    }

    if ((!hasFeedback || !behavior.isAutoScored(model)) && mode !== LessonMode.REVIEW) {
      if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
        result = t('previewWarning2');
      } else if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION) {
        result = t('saveCommit2');
      }
    } else {
      let currentCount = 0;
      const currentUiMode = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
      if (currentUiMode) {
        currentCount = currentUiMode.currentAttemptCount;
      }
      if (typeof behavior.getQuestionFeedbackText !== 'undefined') {
        if (!lessonElementState.lessonElementId) {
          lessonElementState.lessonElementId = lessonElementId;
        }
        result = behavior.getQuestionFeedbackText(model,
          correctFeedback,
          incorrectFeedback,
          modelCorrectFeedbackBody,
          modelIncorrectFeedbackBody,
          mode,
          lessonElementState.correctAnswerShowing ? QuestionFeedbackState.CORRECT : isQuestionCorrect,
          currentCount,
          { lessonElementState });
      }
      if (UtilsService.isNullOrEmpty(result)) {
        result = t('saveCommit2');
      }
    }
    return result;
  }

  initSlideResponses = async () => {
    const questionList = lessonManager.elementsThatHaveState;
    for (const model of questionList) {
      const type = (model.type) ? model.type : null;
      const questionBehavior = createQuestionBehavior(type);
      const { lessonElementId } = model;
      if (!responseManager.lessonElementStates.has(lessonElementId)) {
        this.clearFeedback(lessonElementId);
        const activityId = '';
        const responseId = '';
        const contentObjectId = model.id;
        const viewedTime = 0;
        const scoreNow = false;
        const toolPersistence = {};
        const isSaved = false;
        const isSubmitted = false;
        const isScored = false;
        const scoreState = '';
        const teacherNote = null;
        const markedForReview = false;
        const subscore = null;
        const clearOld = true;
        const responseAnswer = await questionBehavior.getInitializedResponse(lessonElementId, model);

        let maxScore = 0;
        if (model.isTestItem) {
          maxScore = model.maxScore;
        }
        if (!(model.isTestItem || model.isStudentInstruction || model.isActivity || model.isResource || model.isInstruction)) {
          // Then this is not a valid type to have an elementState
          throw new Error('Tried to create lessonElementState for invalid type. Check whether lessonManager.shouldHaveElementState(model) is set correctly.');
        }
        // Now we can create the lessonElementState
        responseManager.createLessonElementState(
          activityId, responseId, maxScore, lessonElementId,
          contentObjectId, responseAnswer, viewedTime, scoreNow,
          toolPersistence, isSaved, isSubmitted, isScored, scoreState, teacherNote, markedForReview,
          subscore, questionBehavior, clearOld, model.isTestItem, 0, 0, model.survey, model.unscored, model.offlineGrading
        );

        if (!questionBehavior.isAutoScored(model) && !model.unscored) {
          responseManager.addNotScoredList(lessonElementId);
        }
      }
    }
  }

  doFeedbackLogic = (lessonElementId, model, mode, lessonElementState) => {
    if (lessonElementState && lessonElementState.isSubmitted && !questionFeedbackManager.noSubmit) {
      responseManager.setReadOnly(lessonElementId, true);
    }
    questionFeedbackManager.setSubmitVisibleState(lessonElementId, false); // Show "Submit" button
    questionFeedbackManager.setTryAgainVisibleState(lessonElementId, false); // Show "Try Again" button
    questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, false);
    questionFeedbackManager.setShowHintVisibleState(lessonElementId, false); // Show "Show Hint" button
    questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, false); // Show "Show solution" button
    questionFeedbackManager.setShowAnswerFeedback(lessonElementId, false);
    questionFeedbackManager.setShowQuestionFeedback(lessonElementId, false); // Show the popup below with the feedback text
    questionFeedbackManager.setShowAnswers(lessonElementId, false); // Swap in real answers
    questionFeedbackManager.setLoadingButtonState(lessonElementId, false);
    questionFeedbackManager.setShowStudentAnswersButtonVisible(lessonElementId, false);

    if (!model || !model.isTestItem || !lessonElementState) {
      return;
    }
    const { hasFeedback, noSubmit } = questionFeedbackManager;
    const { isPractice, practiceAttempts, customPracticeMatrix } = questionFeedbackManager.questionFeedbackMode;
    const { isSubmitted, questionBehavior } = lessonElementState;

    const currentUiMode = questionFeedbackManager.lessonElementFeedbackTracker.get(lessonElementId);
    const { currentAttemptCount } = currentUiMode;
    const hasHint = (!UtilsService.isNullOrEmpty(model.hint));
    const hasSolution = (model.validation && !UtilsService.isNullOrEmpty(model.validation.solution));
    const isAutoScored = questionBehavior.isAutoScored(model);
    const attemptsLeft = currentAttemptCount < practiceAttempts && currentAttemptCount >= 0;
    const isCorrect = (questionBehavior.isQuestionCorrect(lessonElementState, lessonElementId) === QuestionFeedbackState.CORRECT);

    const forceShowAnswers = toolbarManager.isToolbarAnswerFeedbackActive;

    // If the question has a hint or a sample solution we want those to show when the toolbar answers button is clicked.
    if (forceShowAnswers) {
      questionFeedbackManager.setSubmitVisibleState(lessonElementId, false);
      questionFeedbackManager.setTryAgainVisibleState(lessonElementId, false);
      questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, false);
      if (hasHint) {
        questionFeedbackManager.setShowHintVisibleState(lessonElementId, true);
      } else {
        questionFeedbackManager.setShowHintVisibleState(lessonElementId, false);
      }
      if (hasSolution) {
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, true);
      } else {
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, false);
      }
      questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
      questionFeedbackManager.setShowQuestionFeedback(lessonElementId, false);
      questionFeedbackManager.setShowAnswers(lessonElementId, true);

      // If the answer is already showing no need to show it again
      if (!lessonElementState.correctAnswerShowing) {
        const mode = lessonManager.playerMode;
        const wrongPracticeMode = (mode === LessonMode.SCORING || mode === LessonMode.REVIEW);
        const isPracticeRunning = (isPractice && !wrongPracticeMode && (currentAttemptCount < practiceAttempts));
        if (lessonElementState.questionBehavior.isAutoScored(model)) {
          lessonElementState.setCachedResponse(UtilsService.safeMobxClone(lessonElementState.currentResponseAnswer), lessonElementState.submittedItemScore);
          lessonElementState.questionBehavior.setCorrectAnswer(lessonElementState, model, isPracticeRunning);
          lessonElementState.turnOnAnswerShowing();
        }
      }

      return;
    }

    // lessonElementState.maxScore === lessonElementState.submittedItemScore;
    const unscoreNotSaved = (model.unscored && (model.type === ContentType.LONG_TEXT.type || model.type === ContentType.APPLET_QUESTION.type));
    let unscoreSaved = false;
    if (unscoreNotSaved) {
      unscoreSaved = model.unscored && lessonElementState.questionBehavior.checkForValidResponse(lessonElementState.currentResponseAnswer, model);
    }
    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION || mode === LessonMode.PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW || mode === LessonMode.ITEM_PREVIEW) {
      
      // C2C-7658: Hide submit button for alchemie questions if the question is unscored
      const alchemieQuestion = model.type === ContentType.ALCHEMIE_QUESTION.type || model.type === ContentType.ALCHEMIE_TEST_ITEM.type || model.type === ContentType.STAND_ALONE_ALCHEMIE_ITEM.type;
      if (alchemieQuestion && model.unscored) {
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, false);
        return;
      }
      
      /// ////// no feedback or manual or not practice
      if (!isSubmitted && !isPractice && !noSubmit) {
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
        return;
      }

      if (unscoreSaved && (hasFeedback)) {
        const showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.msht);
        const showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.msso);

        responseManager.setReadOnly(lessonElementId, false);
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);

        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
        questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);

        // Announce hint and solution if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }

        return;
      }

      if (unscoreNotSaved && (hasFeedback)) {
        const showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.msht);

        responseManager.setReadOnly(lessonElementId, false);
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);

        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);

        // Announce hint if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }

        return;
      }

      if ((isSubmitted) && ((hasFeedback && !isPractice) || !hasFeedback || !isAutoScored)) {
        const showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.msht);
        const showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.msso);

        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
        if (isAutoScored && hasFeedback) {
          questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        }

        // Announce hint and solution if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }

        return;
      }

      /// ///////////////////////////////////////////////////////////
      /// feedback with practice and autoscore
      if (!isSubmitted && isPractice && attemptsLeft) {
        questionFeedbackManager.setSubmitVisibleState(lessonElementId, true);
        let showHint = false;
        let showSolution = false;

        switch (currentAttemptCount) {
          case 0:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a1bht);
            break;
          case 1:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a2bht);
            break;
          case 2:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a3bht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a3bso);
            break;

          default:
            break;
        }
        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);

        // Announce hint and solution if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }
        return;
      }

      if (isSubmitted && isPractice && !attemptsLeft && !isCorrect) {
        let showHint = false;
        let showSolution = false;
        let showAnswers = false;

        switch (practiceAttempts) {
          case (1): {
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a1iht);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a1ica);
            break;
          }
          case (2): {
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a2iht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a2iso);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a2ica);// using the atempt 2 correct because that's a reasonable result when out of attmpts
            break;
          }
          case (3): {
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a3iht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a3iso);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a3ica);
            break;
          }
          default:
            break;
        }

        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);
        questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, showAnswers);
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
        questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        questionFeedbackManager.setShowStudentAnswersButtonVisible(lessonElementId, true);

        // Announce hint, solution, and answers if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }
        if (showAnswers) {
          accessibilityService.announceShowAnswers();
        }
        return;
      }

      if (isSubmitted && isPractice && !isCorrect && attemptsLeft) {
        let showHint = false;
        let showSolution = false;
        let showAnswers = false;

        questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
        questionFeedbackManager.setTryAgainVisibleState(lessonElementId, true);

        switch (currentAttemptCount) {
          case 1:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a1iht);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a1ica);
            break;
          case 2:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a2iht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a2iso);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a2ica);
            break;
          default:
            break;
        }
        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);
        questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, showAnswers);

        // Announce hint, solution, and answers if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }
        if (showAnswers) {
          accessibilityService.announceShowAnswers();
        }
        return;
      }

      if ((isSubmitted && isPractice && isCorrect)) {
        let showHint = false;
        let showSolution = false;
        let showAnswers = false;

        questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
        questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);

        switch (currentAttemptCount) {
          case -1:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a1cht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a1cso);
            break;
          case 1:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a1cht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a1cso);
            showAnswers = false; // If the student got it right on their first attempt, don't show the answers
            questionFeedbackManager.setCurrentAttemptCount(lessonElementId, -1);
            break;
          case 2:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a2cht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a2cso);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a2cca);
            questionFeedbackManager.setCurrentAttemptCount(lessonElementId, practiceAttempts);
            break;
          case 3:
            showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.a3cht);
            showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.a3cso);
            showAnswers = customPracticeMatrix.get(QFMFlagKeys.a3cca);
            questionFeedbackManager.setCurrentAttemptCount(lessonElementId, practiceAttempts);
            break;
          default:
            break;
        }
        questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
        questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);
        questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, showAnswers);
        questionFeedbackManager.setShowStudentAnswersButtonVisible(lessonElementId, true);

        // Announce hint, solution, and answers if shown
        if (showHint) {
          accessibilityService.announceShowHint();
        }
        if (showSolution) {
          accessibilityService.announceShowSolution();
        }
        if (showAnswers) {
          accessibilityService.announceShowAnswers();
        }
      }
    } else if (mode === LessonMode.REVIEW) {
      const showAnswers = customPracticeMatrix.get(QFMFlagKeys.rmca) && isAutoScored;
      const showHint = hasHint && customPracticeMatrix.get(QFMFlagKeys.rmht);
      const showSolution = hasSolution && customPracticeMatrix.get(QFMFlagKeys.rmso);
      questionFeedbackManager.setShowAnswerFeedback(lessonElementId, true);
      questionFeedbackManager.setShowQuestionFeedback(lessonElementId, true);
      questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, showAnswers);
      questionFeedbackManager.setShowHintVisibleState(lessonElementId, showHint);
      questionFeedbackManager.setShowStudentAnswersButtonVisible(lessonElementId, showAnswers);
      questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, showSolution);

      // Announce hint, solution, and answers if shown
      if (showHint) {
        accessibilityService.announceShowHint();
      }
      if (showSolution) {
        accessibilityService.announceShowSolution();
      }
      if (showAnswers) {
        accessibilityService.announceShowAnswers();
      }
    } else if (mode === LessonMode.SCORING) {
      const showAnswers = isAutoScored;
      questionFeedbackManager.setShowAnswerFeedback(lessonElementId, lessonElementState.isSubmitted);
      questionFeedbackManager.setShowQuestionFeedback(lessonElementId, lessonElementState.isSubmitted);
      questionFeedbackManager.setShowAnswersVisibleState(lessonElementId, showAnswers);
      questionFeedbackManager.setShowStudentAnswersButtonVisible(lessonElementId, showAnswers);
      questionFeedbackManager.setShowHintVisibleState(lessonElementId, hasHint);
      questionFeedbackManager.setShowSolutionVisibleState(lessonElementId, hasSolution);

      // Announce hint, solution, and answers if shown
      if (hasHint) {
        accessibilityService.announceShowHint();
      }
      if (hasSolution) {
        accessibilityService.announceShowSolution();
      }
      if (showAnswers) {
        accessibilityService.announceShowAnswers();
      }
    }
  }

  setShowFeedbackImages = (lessonElementId, toggle) => {
    questionFeedbackManager.setShowFeedbackImages(lessonElementId, toggle);
  }

  setSolutionDialogOpen = (flag) => {
    questionFeedbackManager.setSolutionDialogOpen(flag);
    if (flag) {
      questionFeedbackManager.addButtonUsage('showSolution');
      accessibilityService.announceShowSolution();
    }
  }

  setHintDialogOpen = (flag) => {
    questionFeedbackManager.setHintDialogOpen(flag);
    if (flag) {
      questionFeedbackManager.addButtonUsage('showHint');
      accessibilityService.announceShowHint();
    }
  }

  setupRuntimeDependency = () => {
    const enforce = responseManager.sequentialItemDependency || responseManager.dependencyFlowMap.size > 0;
    if (enforce) {
      if (responseManager.sequentialItemDependency) {
        const questionList = lessonManager.elementsThatHaveState;
        questionList.forEach((model, index) => {
          if (index > 0 && !model.isInstruction && !model.isActivity) {
            let previousIndex = index - 1;
            const lessonElementState = responseManager.getLessonElementState(model.lessonElementId);
            let controllerModel = questionList[previousIndex];
            while ((controllerModel.isInstruction || controllerModel.isActivity) && previousIndex > 0) {
              previousIndex -= 1;
              controllerModel = questionList[previousIndex];
            }
            if (controllerModel.isInstruction || controllerModel.isActivity) {
              return;
            }
            const controllerState = responseManager.getLessonElementState(controllerModel.lessonElementId);

            if (controllerModel && controllerState && lessonElementState) {
              if (!controllerState.isSubmitted) {
                lessonElementState.addController(controllerModel.lessonElementId);
              }
              controllerState.addDependent(model.lessonElementId);
            }
          }
        });
      } else {
        const iterator = responseManager.dependencyFlowMap.keys();
        let next = iterator.next();
        while (!next.done) {
          const lessonElementId = next.value;
          const lessonElement = lessonManager.getLessonElement(lessonElementId);
          if (lessonElement && !UtilsService.isNullOrEmpty(lessonElementId)) {
            const controllerArray = responseManager.dependencyFlowMap.get(lessonElementId);
            const dependentState = responseManager.getLessonElementState(lessonElementId);

            if (dependentState && controllerArray && controllerArray.length > 0) {
              controllerArray.forEach((id) => {
                const controllerState = responseManager.getLessonElementState(id);
                if (!controllerState.isSubmitted) {
                  dependentState.addController(id);
                  controllerState.addDependent(lessonElementId);
                }
                if (lessonElement.lessonElementIds && lessonElement.lessonElementIds.length > 0) {
                  lessonElement.lessonElementIds.forEach((childId) => {
                    if (!controllerState.isSubmitted) {
                      const childState = responseManager.getLessonElementState(childId);
                      childState.addController(id);
                      controllerState.addDependent(childId);
                    }
                  });
                }
              });
            }
          }
          next = iterator.next();
        }
      }
    }
  }

  alertEmptyPreviousResponse = async () => {
    const { confirmDialog } = await import('../components/dialogs');
    return await confirmDialog({
      title: t('emptyPreviousResponseTitle'),
      text: t('emptyPreviousResponseMessage'),
      showCancelButton: false,
      confirmButtonText: t('ok')
    });
  }

  alertDependencyLock = async (lessonElementState) => {
    // eslint-disable-next-line no-return-await
    return await dependentAlert({ lessonElementState });
  }
}

export default new ResponseService();
